import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Trophy, Crown, Medal, Award, Loader2 } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const PERIODS = ["Trimestre 1", "Trimestre 2", "Trimestre 3"];

export default function RankingsPage() {
  const [classes, setClasses] = useState([]);
  const [classId, setClassId] = useState("");
  const [period, setPeriod] = useState("");
  const [rankings, setRankings] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/classes").then(({ data }) => {
      setClasses(data);
      if (data.length > 0) setClassId(data[0].id);
    });
  }, []);

  useEffect(() => {
    if (!classId) return;
    setLoading(true);
    api.get(`/classes/${classId}/rankings${period ? `?period=${encodeURIComponent(period)}` : ""}`)
      .then(({ data }) => setRankings(data))
      .finally(() => setLoading(false));
  }, [classId, period]);

  const podiumIcon = (rank) => {
    if (rank === 1) return { icon: Crown, color: "#FBBF24", label: "Or" };
    if (rank === 2) return { icon: Medal, color: "#94A3B8", label: "Argent" };
    if (rank === 3) return { icon: Award, color: "#CD7F32", label: "Bronze" };
    return null;
  };

  return (
    <div className="space-y-5" data-testid="page-rankings">
      <div>
        <div className="chip mb-2"><Trophy className="h-3 w-3" /><span>Classements</span></div>
        <h2 className="font-heading text-3xl font-extrabold text-[#1E3A8A] tracking-tight">Classement par classe</h2>
        <p className="text-sm text-slate-500 mt-1">Performances individuelles, podium et moyennes.</p>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-5 grid md:grid-cols-2 gap-4">
        <div>
          <label className="text-xs font-bold uppercase tracking-wider text-slate-500 block mb-1">Classe</label>
          <Select value={classId} onValueChange={setClassId}>
            <SelectTrigger className="w-full" data-testid="rankings-class-select"><SelectValue placeholder="— Choisir —" /></SelectTrigger>
            <SelectContent>
              {classes.map(c => <SelectItem key={c.id} value={c.id}>{c.name} · {c.level}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs font-bold uppercase tracking-wider text-slate-500 block mb-1">Période</label>
          <Select value={period || "all"} onValueChange={(v) => setPeriod(v === "all" ? "" : v)}>
            <SelectTrigger className="w-full" data-testid="rankings-period-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toute l&apos;année</SelectItem>
              {PERIODS.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading && <div className="text-center py-10"><Loader2 className="h-6 w-6 animate-spin mx-auto text-slate-400" /></div>}

      {!loading && rankings.length === 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6 text-center text-sm text-yellow-800">
          Aucune note enregistrée pour cette classe / période.
        </div>
      )}

      {!loading && rankings.length > 0 && (
        <>
          {/* Podium */}
          <div className="grid grid-cols-3 gap-3 md:gap-5">
            {rankings.slice(0, 3).map((r, i) => {
              const p = podiumIcon(r.rank);
              if (!p) return null;
              const height = r.rank === 1 ? "h-44" : r.rank === 2 ? "h-36" : "h-32";
              return (
                <div
                  key={r.student_id}
                  className={`bg-gradient-to-b from-white to-slate-50 border-2 rounded-2xl p-4 ${height} flex flex-col justify-end items-center text-center shadow-md`}
                  style={{ borderColor: p.color, order: r.rank === 1 ? 2 : r.rank === 2 ? 1 : 3 }}
                  data-testid={`podium-${r.rank}`}
                >
                  <div className="w-14 h-14 rounded-full flex items-center justify-center mb-2 shadow-lg" style={{ backgroundColor: p.color }}>
                    <p.icon className="h-7 w-7 text-white" />
                  </div>
                  <div className="text-xs uppercase tracking-wider font-bold" style={{ color: p.color }}>{p.label} · #{r.rank}</div>
                  <div className="font-heading font-bold text-sm text-[#0F172A] truncate w-full mt-1">{r.name}</div>
                  <div className="font-heading font-extrabold text-2xl text-[#166534]">{r.average.toFixed(2)}</div>
                  <div className="text-[10px] text-slate-500">{r.grades_count} notes</div>
                </div>
              );
            })}
          </div>

          {/* Liste complète */}
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <div className="p-4 border-b border-slate-200">
              <h3 className="font-heading font-bold text-[#1E3A8A]">Classement complet</h3>
              <p className="text-xs text-slate-500">{rankings.length} élève(s) classé(s)</p>
            </div>
            <table className="w-full">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="px-5 py-3 text-left">Rang</th>
                  <th className="px-5 py-3 text-left">Élève</th>
                  <th className="px-5 py-3 text-center">Notes</th>
                  <th className="px-5 py-3 text-right">Moyenne</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rankings.map(r => {
                  const p = podiumIcon(r.rank);
                  return (
                    <tr key={r.student_id} className="hover:bg-slate-50" data-testid={`ranking-row-${r.student_id}`}>
                      <td className="px-5 py-3">
                        <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-xs font-bold ${p ? "text-white" : "bg-slate-100 text-slate-600"}`} style={p ? { backgroundColor: p.color } : {}}>
                          {r.rank}
                        </span>
                      </td>
                      <td className="px-5 py-3 text-sm font-semibold text-[#0F172A]">{r.name}</td>
                      <td className="px-5 py-3 text-center text-xs text-slate-500">{r.grades_count}</td>
                      <td className="px-5 py-3 text-right">
                        <span className={`font-heading font-extrabold text-base ${r.average >= 14 ? "text-[#166534]" : r.average >= 10 ? "text-[#FBBF24]" : "text-[#EA580C]"}`}>
                          {r.average.toFixed(2)}/20
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
